import * as React from "react";
import { useEffect, useState } from "react";
import { PublicClientApplication } from "@azure/msal-browser";
import forge from "node-forge";
import { MessageBar, MessageBarBody, MessageBarTitle, Spinner } from "@fluentui/react-components";
import { SelectableSignatures } from "./SelectableSignatures";
import Login from "./Login";
import { useStateStore } from "../../store";
import { aes256CbcDecrypt, aes256CbcEncrypt, encryptData, getPublicKey } from "../../utils/encryption";
import { t } from "../../utils/translations";

const TaskPane = () => {
  const [msalInstance, setMsalInstance] = useState(null);
  const email = Office?.context?.mailbox?.userProfile?.emailAddress;
  const {
    status,
    setStatus,
    description,
    forceLogin,
    setDescription,
    error,
    setError,
    setFirmas,
    apinetToken,
    setApinetToken,
    removeLocalStorageItem,
  } = useStateStore((state) => ({
    status: state.status,
    setStatus: state.setStatus,
    description: state.description,
    forceLogin: state.forceLogin,
    setDescription: state.setDescription,
    error: state.error,
    setError: state.setError,
    setFirmas: state.setFirmas,
    apinetToken: state.apinetToken,
    setApinetToken: state.setApinetToken,
    removeLocalStorageItem: state.removeLocalStorageItem,
  }));

  const [token, setToken] = useState(null);

  const updateFirmas = (newFirmas) => {
    setFirmas(newFirmas);
  };

  useEffect(() => {
    const initializeFlow = async () => {
      if (apinetToken) {
        await loginWithApinetToken(apinetToken);
      } else if (!forceLogin) {
        initializeMsalAndAttemptSSO();
      } else {
        setStatus("loginfailed");
      }
    };

    initializeFlow();
  }, []);

  const loginWithApinetToken = async (apinetToken) => {
    setStatus("loading");
    setDescription(t("loggingIn"));

    const url = "https://www.know.ee/apinet/v3/users/authenticate";

    try {
      const aesEncryptKey = "kKK7vEqA/nNQFCVW4yymITp4HG1FjT4577N37L1s5qQ=";
      const key = forge.util.decode64(aesEncryptKey);
      const iv = forge.random.getBytesSync(16);
      const certPem = process.env.CERTIFICATE;
      const publicKey = getPublicKey(certPem);
      const encryptedApinetToken = aes256CbcEncrypt(apinetToken, key, iv);

      const data = {
        user: {
          apinet_token: encryptedApinetToken,
        },
        item: encryptData(iv, publicKey),
      };

      const options = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization:
            'Token token="UH/8s1fH3i9gVriUlK2dDKWfTpk+6zj8Ve1aE1uav2cv03/DpJJfaAVho+6f\\nQIZnheT4D2c7sut3mgtOmh69f3W0C8RLtLWA0+gE+nsp+sM=\\n"',
        },
        body: JSON.stringify(data),
      };

      const response = await fetch(url, options);
      const responseData = await response.json();
      if (response.status === 200) {
        const decryptedSignatures = responseData.signatures.map((signature) => {
          return {
            ...signature,
            name: aes256CbcDecrypt(signature.name, key, iv),
            id_file: aes256CbcDecrypt(signature.id_file, key, iv),
            // We leave file, background_meeting and background_preview encrypted for lazy decryption
            iv: forge.util.encode64(iv),
            key: forge.util.encode64(key),
            isEncrypted: true,
          };
        });

        updateFirmas(decryptedSignatures);
        setStatus("success");
      } else {
        setStatus("backend_error");
        initializeMsalAndAttemptSSO();
      }
    } catch (error) {
      setStatus("backend_error");
      initializeMsalAndAttemptSSO();
    }
  };

  const initializeMsalAndAttemptSSO = () => {
    const msalConfig = {
      auth: {
        clientId: "fd55f070-245e-49db-b991-b84be7149d52",
        authority: "https://login.microsoftonline.com/common",
        redirectUri: "https://outlook-signatures.know.ee/taskpane.html",
        clientSecret: "eVB8Q~YZm6uM16rThtJ88osRelXgQh3x9g6NmdnD",
      },
      cache: {
        cacheLocation: "sessionStorage",
        storeAuthStateInCookie: true,
      },
    };

    setMsalInstance(new PublicClientApplication(msalConfig));
    setStatus("loading");
  };

  const attemptSilentLogin = async (instance) => {
    setDescription(t("ssoLoggingIn"));
    const request = {
      scopes: ["user.read", "profile", "openid"],
      loginHint: email,
    };
    try {
      const silentResponse = await instance.ssoSilent(request);
      console.log("silentResponse", silentResponse);
      const accessToken = silentResponse.accessToken;
      setToken(accessToken);
      setDescription(t("accessTokenObtained"));
      setError(null);
      await callBackendSSO(accessToken);
    } catch (error) {
      console.log("Error al iniciar sesión automáticamente", error);
      setError(error);
      try {
        setDescription(t("popupLoggingIn"));
        const popupResponse = await instance.loginPopup(request);
        console.log("popupResponse", popupResponse);
        const accessToken = popupResponse.accessToken;
        setToken(accessToken);
        setDescription(t("accessTokenObtained"));
        setError(null);
        await callBackendSSO(accessToken);
      } catch (popupError) {
        console.log("Error al iniciar sesión mediante popup", popupError);
        setStatus("ssofailed");
        setDescription(t("ssoFailed"));
        setError(t("errorSSOmessage"));
      }
    }
  };

  useEffect(() => {
    if (!msalInstance) return;

    const initializeAndAttemptLogin = async () => {
      try {
        await msalInstance.initialize();
        console.log("MSAL instance initialized");
        await attemptSilentLogin(msalInstance);
      } catch (error) {
        console.error("Error during initialize and login:", error);
        setStatus("ssofailed");
        setDescription(t("ssoFailed"));
        setError(t("errorSSOmessage"));
      }
    };

    initializeAndAttemptLogin();
  }, [msalInstance]);

  const handleTrySso = () => {
    setStatus("loading");
    initializeMsalAndAttemptSSO();
  };

  const callBackendSSO = (jwtToken) => {
    return new Promise((resolve, reject) => {
      const url = "https://www.know.ee/apinet/v4/users/signatures";
      const aesEncryptKey = "kKK7vEqA/nNQFCVW4yymITp4HG1FjT4577N37L1s5qQ=";
      const key = forge.util.decode64(aesEncryptKey);
      const iv = forge.random.getBytesSync(16);
      const certPem = process.env.CERTIFICATE;
      const publicKey = getPublicKey(certPem);
      const options = {
        method: "GET",
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          "X-Encrypted-Item": encryptData(iv, publicKey),
        },
      };
      setDescription(t("gettingSignatures"));
      fetch(url, options)
        .then((response) => {
          if (!response.ok) {
            throw new Error("Error al llamar a la API");
          }
          return response.json();
        })
        .then((responseData) => {
          // Procesa los datos de firmas obtenidos de la respuesta de tu API
          const signatures = responseData.signatures;
          removeLocalStorageItem("forceLogin");
          setFirmas(signatures);
          if (responseData.apinet_token) {
            setApinetToken(aes256CbcDecrypt(responseData.apinet_token, key, iv));
          }
          setStatus("success");
          setDescription(t("signaturesObtained"));
          setError(null);
          resolve(signatures);
        })
        .catch((error) => {
          console.error("Error al obtener firmas", error);
          setStatus("ssofailed");
          setDescription(t("ssoFailed"));
          setError(t("errorSSOmessage"));
          reject(error);
        });
    });
  };

  if (status === "loading") {
    return (
      <div style={styles.container}>
        <Spinner labelPosition="below" label={description} />
      </div>
    );
  } else if (status === "success") {
    return (
      <>
        <SelectableSignatures />
      </>
    );
  } else if (status === "ssofailed" || status === "loginfailed") {
    return (
      <>
        {error && (
          <MessageBar intent="error" style={{ marginLeft: "10px", marginRight: "10px", marginBottom: "10px" }}>
            <MessageBarBody>
              <MessageBarTitle>{error}</MessageBarTitle>
            </MessageBarBody>
          </MessageBar>
        )}
        <Login onTrySso={handleTrySso} />
      </>
    );
  } else {
    return (
      <div style={styles.container}>
        <Spinner labelPosition="below" label={description} />
      </div>
    );
  }
};

const styles = {
  container: {
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  divider: {
    margin: "10px",
  },
  loginSsoContainer: {
    width: "100%",
    padding: "10px",
  },
  itemContainer: {
    width: "100%",
    padding: "10px",
  },
};

export default TaskPane;
